/**
 * Build cross-narrowed Client / Site / Officer option lists.
 *
 * - Client and Site are linked through `site.client_name`.
 * - Officer ↔ Site is derived from shift assignments (`shift.guard_ids` × `shift.site_id`).
 * - Each dimension's options are narrowed by the *other* dimensions only —
 *   this keeps the dimension's own multi-select usable (you can still pick more
 *   in the same dimension even after picking one).
 */
export function buildLinkedOptions({ clients = [], sites = [], guards = [], shifts = [], selected }) {
  const { client_names = [], site_ids = [], guard_ids = [] } = selected || {};

  // ── Linkage maps (from shift assignments)
  const guardToSites = new Map(); // guardId -> Set<siteId>
  const siteToGuards = new Map(); // siteId  -> Set<guardId>
  for (const s of shifts) {
    const sid = s.site_id;
    const gids = Array.isArray(s.guard_ids) ? s.guard_ids : (s.guard_id ? [s.guard_id] : []);
    if (!sid) continue;
    for (const gid of gids) {
      if (!gid) continue;
      if (!guardToSites.has(gid)) guardToSites.set(gid, new Set());
      guardToSites.get(gid).add(sid);
      if (!siteToGuards.has(sid)) siteToGuards.set(sid, new Set());
      siteToGuards.get(sid).add(gid);
    }
  }

  // Helper: site → client_name (fast lookup)
  const siteIdToClient = new Map(sites.map((s) => [s.id, s.client_name || ""]));

  // ── Compute *allowed* sets per dimension based on the other dimensions
  // Sites allowed by selected clients (or all if no client selected)
  const sitesAllowedByClient = client_names.length === 0
    ? null
    : new Set(sites.filter((s) => client_names.includes(s.client_name || "")).map((s) => s.id));
  // Sites allowed by selected guards (or all if no guard selected)
  const sitesAllowedByGuard = guard_ids.length === 0
    ? null
    : new Set(guard_ids.flatMap((gid) => Array.from(guardToSites.get(gid) || [])));
  // Officers allowed by selected sites (or all if no site selected)
  const guardsAllowedBySite = site_ids.length === 0
    ? null
    : new Set(site_ids.flatMap((sid) => Array.from(siteToGuards.get(sid) || [])));
  // Officers allowed by selected clients (any site of that client)
  const guardsAllowedByClient = (() => {
    if (client_names.length === 0) return null;
    const allowed = new Set();
    for (const sid of sitesAllowedByClient || []) {
      for (const gid of siteToGuards.get(sid) || []) allowed.add(gid);
    }
    return allowed;
  })();
  // Clients allowed by selected sites
  const clientsAllowedBySite = site_ids.length === 0
    ? null
    : new Set(site_ids.map((sid) => siteIdToClient.get(sid)).filter(Boolean));
  // Clients allowed by selected guards (via their sites)
  const clientsAllowedByGuard = guard_ids.length === 0
    ? null
    : new Set(guard_ids.flatMap((gid) => Array.from(guardToSites.get(gid) || []).map((sid) => siteIdToClient.get(sid)).filter(Boolean)));

  const intersectSet = (a, b) => {
    if (a === null) return b;
    if (b === null) return a;
    const out = new Set();
    for (const v of a) if (b.has(v)) out.add(v);
    return out;
  };

  // ── Build option lists (each dimension narrowed by the OTHER two)
  const clientAllowed = intersectSet(clientsAllowedBySite, clientsAllowedByGuard);
  const clientOptions = clients
    .filter((name) => clientAllowed === null || clientAllowed.has(name))
    .map((name) => ({ value: name, label: name }));

  const siteAllowed = intersectSet(sitesAllowedByClient, sitesAllowedByGuard);
  const siteOptions = sites
    .filter((s) => siteAllowed === null || siteAllowed.has(s.id))
    .map((s) => ({ value: s.id, label: s.name || "Unnamed", sub: s.client_name || undefined }));

  const guardAllowed = intersectSet(guardsAllowedBySite, guardsAllowedByClient);
  const guardOptions = guards
    .filter((g) => guardAllowed === null || guardAllowed.has(g.id))
    .map((g) => ({ value: g.id, label: g.name || "Unnamed", sub: g.badge_no ? `#${g.badge_no}` : undefined }));

  return { clientOptions, siteOptions, guardOptions };
}

/**
 * Returns a *next* selection object with any selected values that fell out
 * of scope dropped. Use this to self-heal selections when the underlying
 * data changes (e.g. switching a client filter removes some sites).
 */
export function pruneSelections(selected, options) {
  const next = { ...selected };
  const filter = (arr, opts) => {
    const valid = new Set(opts.map((o) => o.value));
    const kept = arr.filter((v) => valid.has(v));
    return kept.length === arr.length ? arr : kept;
  };
  next.client_names = filter(selected.client_names || [], options.clientOptions);
  next.site_ids = filter(selected.site_ids || [], options.siteOptions);
  next.guard_ids = filter(selected.guard_ids || [], options.guardOptions);
  return next;
}
